/**
 * Snow thrown up by a vehicle: lumps flung off the tyres in a fan behind, a
 * rooster tail from wheels spinning in place, a sheet out to the side in a
 * slide, a bow wave ploughed up by the nose in deep snow, and a cloud of
 * powder hanging in the air behind it all.
 *
 * Two instanced meshes of faceted balls, as the world and its smoke are
 * drawn: small solid lumps that arc and fall back to the snow, and big pale
 * puffs of powder that swell, drift and thin away. How much a tyre throws is
 * `sprayRate` (`core/weather/snow.ts`).
 */

import { Color, DynamicDrawUsage, IcosahedronGeometry, InstancedMesh, Matrix4, MeshStandardMaterial, Quaternion, Vector3 } from 'three';

/** Most of each in the air at once. */
const MOST_LUMPS = 1200;
const MOST_POWDER = 320;
const GRAVITY = 9.81;

interface Bit {
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  /** The snow's surface it came from: where a lump lands. */
  floor: number;
  age: number; life: number;
  size: number;
  spin: number;
}

const matrix = new Matrix4();
const at = new Vector3();
const axis = new Vector3();
const turn = new Quaternion();
const scale = new Vector3();

/** Where a vehicle is and how it moves, for its spray. */
export interface Mover {
  /** Velocity, scene m/s. */
  vx: number;
  vz: number;
  /** Unit forward, scene x and z. */
  fx: number;
  fz: number;
}

export class SnowSpray {
  readonly lumps: InstancedMesh;
  readonly powder: InstancedMesh;
  private readonly lumpBits: Bit[] = [];
  private readonly powderBits: Bit[] = [];
  /** Lumps owed to each emitter, carried between frames. */
  private readonly owed = new Map<string, number>();

  constructor() {
    const lumpMaterial = new MeshStandardMaterial({ color: new Color(0.93, 0.95, 1), roughness: 0.75, flatShading: true });
    this.lumps = new InstancedMesh(new IcosahedronGeometry(1, 0), lumpMaterial, MOST_LUMPS);
    const powderMaterial = new MeshStandardMaterial({
      color: new Color(0.95, 0.97, 1), roughness: 1, flatShading: true, transparent: true, opacity: 0.24, depthWrite: false,
    });
    this.powder = new InstancedMesh(new IcosahedronGeometry(1, 1), powderMaterial, MOST_POWDER);
    for (const mesh of [this.lumps, this.powder]) {
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      mesh.count = 0;
      mesh.frustumCulled = false;
      mesh.castShadow = false;
    }
  }

  /** How many to make now for `key`, at `rate` a second over `dt`, the fraction kept for next time. */
  private due(key: string, rate: number, dt: number): number {
    const owed = (this.owed.get(key) ?? 0) + rate * dt;
    const whole = Math.floor(owed);
    this.owed.set(key, owed - whole);
    return whole;
  }

  /**
   * Snow off a tyre at scene (x, y, z), `rate` lumps a second, from a
   * vehicle moving as `m`. `spin` 0..1 is the wheel spinning faster than it
   * rolls (a rooster tail behind, even standing); `slide` m/s, positive to
   * the right, throws it out to the side.
   */
  tyre(key: string, x: number, y: number, z: number, m: Mover, rate: number, spin: number, slide: number, dt: number): void {
    const n = this.due(key, rate, dt);
    // Right of forward (fx, fz) is (−fz, fx).
    const rx = -m.fz, rz = m.fx;
    const speed = Math.hypot(m.vx, m.vz);
    for (let i = 0; i < n && this.lumpBits.length < MOST_LUMPS; i++) {
      // Off the tread: left behind at the car's speed less a little, up into
      // a fan, and flung back hard by a spinning wheel.
      // A spinning wheel throws it back hard and low, out behind the car,
      // not up over the roof.
      const back = 1.5 + 9 * spin * (0.6 + 0.4 * Math.random());
      const up = 1.2 + Math.random() * (1.5 + 0.12 * speed + 1.2 * spin);
      const across = (Math.random() - 0.5) * (1.6 + 0.08 * speed) + slide * 0.6 * Math.random();
      const keep = 0.55 + 0.25 * Math.random();
      this.lumpBits.push({
        x: x + (Math.random() - 0.5) * 0.2, y: y + 0.05, z: z + (Math.random() - 0.5) * 0.2,
        vx: m.vx * keep - m.fx * back + rx * across, vy: up, vz: m.vz * keep - m.fz * back + rz * across,
        floor: y, age: 0, life: 1.6, size: 0.025 + Math.random() * 0.045, spin: Math.random() * 10,
      });
    }
    // Powder: one puff for every few lumps, hanging in the air behind.
    const puffs = this.due(`${key}:powder`, rate * 0.12 * (0.4 + Math.min(1, speed / 15) + spin), dt);
    for (let i = 0; i < puffs && this.powderBits.length < MOST_POWDER; i++) {
      this.powderBits.push({
        x: x - m.fx * 0.4, y: y + 0.15, z: z - m.fz * 0.4,
        vx: m.vx * 0.35 + (Math.random() - 0.5) * 0.8 - m.fx * 2 * spin, vy: 0.3 + Math.random() * 0.5,
        vz: m.vz * 0.35 + (Math.random() - 0.5) * 0.8 - m.fz * 2 * spin,
        floor: y, age: 0, life: 1.4 + Math.random() * 1.2, size: 0.16 + Math.random() * 0.14, spin: Math.random() * 10,
      });
    }
  }

  /**
   * The nose ploughing through deep snow at scene (x, y, z), `amount` 0..1:
   * a bow wave thrown up and ahead, spilling to either side.
   */
  plough(key: string, x: number, y: number, z: number, m: Mover, amount: number, halfWidth: number, dt: number): void {
    const speed = Math.hypot(m.vx, m.vz);
    const n = this.due(key, amount * (20 + 9 * speed), dt);
    const rx = -m.fz, rz = m.fx;
    for (let i = 0; i < n && this.lumpBits.length < MOST_LUMPS; i++) {
      const s = (Math.random() * 2 - 1);
      const out = s * (0.8 + 0.12 * speed);
      this.lumpBits.push({
        x: x + rx * s * halfWidth, y: y + 0.1, z: z + rz * s * halfWidth,
        vx: m.vx * (1.05 + 0.15 * Math.random()) + rx * out, vy: 1 + Math.random() * (1 + 0.15 * speed),
        vz: m.vz * (1.05 + 0.15 * Math.random()) + rz * out,
        floor: y, age: 0, life: 1.6, size: 0.04 + Math.random() * 0.06, spin: Math.random() * 10,
      });
    }
    const puffs = this.due(`${key}:powder`, amount * (2 + speed), dt);
    for (let i = 0; i < puffs && this.powderBits.length < MOST_POWDER; i++) {
      const s = Math.random() * 2 - 1;
      this.powderBits.push({
        x: x + rx * s * halfWidth, y: y + 0.4, z: z + rz * s * halfWidth,
        vx: m.vx * 0.8 + rx * s, vy: 0.4 + Math.random() * 0.4, vz: m.vz * 0.8 + rz * s,
        floor: y, age: 0, life: 1.2 + Math.random(), size: 0.3 + Math.random() * 0.25, spin: Math.random() * 10,
      });
    }
  }

  /** Move everything in the air on by `dt`, `wind` (scene m/s) carrying the powder. */
  update(dt: number, wind: { x: number; z: number }): void {
    // Lumps: thrown, slowed by the air, falling, gone where they land.
    let n = 0;
    const drag = Math.exp(-dt * 1.4);
    for (let i = this.lumpBits.length - 1; i >= 0; i--) {
      const b = this.lumpBits[i];
      b.age += dt;
      b.vx *= drag; b.vz *= drag;
      b.vy -= GRAVITY * dt;
      b.x += b.vx * dt; b.y += b.vy * dt; b.z += b.vz * dt;
      if (b.age >= b.life || (b.vy < 0 && b.y < b.floor)) {
        this.lumpBits[i] = this.lumpBits[this.lumpBits.length - 1];
        this.lumpBits.pop();
        continue;
      }
      this.place(this.lumps, n++, b, b.size * (1 - Math.max(0, b.age / b.life - 0.7) / 0.3));
    }
    this.lumps.count = n;
    // Powder: slowed hard by the air, settling slowly, drifting downwind, swelling and thinning away.
    n = 0;
    const thick = Math.exp(-dt * 3);
    for (let i = this.powderBits.length - 1; i >= 0; i--) {
      const b = this.powderBits[i];
      b.age += dt;
      if (b.age >= b.life) {
        this.powderBits[i] = this.powderBits[this.powderBits.length - 1];
        this.powderBits.pop();
        continue;
      }
      b.vx = wind.x * 0.6 + (b.vx - wind.x * 0.6) * thick;
      b.vz = wind.z * 0.6 + (b.vz - wind.z * 0.6) * thick;
      b.vy = b.vy * thick - 0.15 * dt;
      b.x += b.vx * dt; b.y = Math.max(b.floor + 0.1, b.y + b.vy * dt); b.z += b.vz * dt;
      const t = b.age / b.life;
      this.place(this.powder, n++, b, b.size * (1 + 2.6 * t) * (1 - t * t * t));
    }
    this.powder.count = n;
    for (const mesh of [this.lumps, this.powder]) mesh.instanceMatrix.needsUpdate = true;
    // Emitters gone quiet a while forget what they owed.
    if (this.owed.size > 64) this.owed.clear();
  }

  private place(mesh: InstancedMesh, i: number, b: Bit, r: number): void {
    at.set(b.x, b.y, b.z);
    axis.set(0.3, 1, 0.2).normalize();
    turn.setFromAxisAngle(axis, b.spin + b.age * 4);
    scale.set(r, r * 0.8, r);
    matrix.compose(at, turn, scale);
    mesh.setMatrixAt(i, matrix);
  }
}
